import { useState, useCallback } from 'react'
import { Building2, Plus, Search, Pencil, Trash2, ChevronLeft, ChevronRight, ShieldCheck } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Select } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import VendorForm from '@/components/features/vendors/VendorForm'
import VendorDetail from '@/components/features/vendors/VendorDetail'
import { vendorsApi } from '@/lib/api/vendors'
import type { Vendor } from '@portal/types'

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'SUPPLIER', label: 'Supplier' },
  { value: 'CONTRACTOR', label: 'Contractor' },
  { value: 'UTILITY', label: 'Utility' },
  { value: 'RENT', label: 'Rent' },
  { value: 'OTHER', label: 'Other' },
]

const PAGE_LIMIT = 20

function categoryBadge(cat: string) {
  const map: Record<string, string> = {
    SUPPLIER: 'text-blue-700 border-blue-300 bg-blue-50',
    CONTRACTOR: 'text-purple-700 border-purple-300 bg-purple-50',
    UTILITY: 'text-orange-700 border-orange-300 bg-orange-50',
    RENT: 'text-teal-700 border-teal-300 bg-teal-50',
    OTHER: 'text-slate-600 border-slate-300 bg-slate-50',
  }
  const labels: Record<string, string> = {
    SUPPLIER: 'Supplier',
    CONTRACTOR: 'Contractor',
    UTILITY: 'Utility',
    RENT: 'Rent',
    OTHER: 'Other',
  }
  return (
    <Badge variant="outline" className={`text-xs ${map[cat] ?? ''}`}>
      {labels[cat] ?? cat}
    </Badge>
  )
}

// ─── Delete confirm dialog ────────────────────────────────────────────────────

interface DeleteDialogProps {
  vendor: Vendor | null
  open: boolean
  onClose: () => void
  onConfirm: () => void
  isPending: boolean
}

function DeleteDialog({ vendor, open, onClose, onConfirm, isPending }: DeleteDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete Vendor</DialogTitle>
          <DialogDescription>
            Remove <strong>{vendor?.name}</strong>? This is a soft delete — payment history is
            preserved.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isPending}>
            {isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── VendorsPage ──────────────────────────────────────────────────────────────

export default function VendorsPage() {
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [category, setCategory] = useState('')
  const [page, setPage] = useState(1)

  const [formOpen, setFormOpen] = useState(false)
  const [editVendor, setEditVendor] = useState<Vendor | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Vendor | null>(null)

  // Debounce search
  const handleSearchChange = useCallback((val: string) => {
    setSearch(val)
    setPage(1)
    const timer = setTimeout(() => setDebouncedSearch(val), 350)
    return () => clearTimeout(timer)
  }, [])

  const { data, isLoading } = useQuery({
    queryKey: ['vendors', debouncedSearch, category, page],
    queryFn: () =>
      vendorsApi.list({
        search: debouncedSearch || undefined,
        category: category || undefined,
        page,
        limit: PAGE_LIMIT,
      }),
    placeholderData: (prev) => prev,
  })

  const vendors: Vendor[] = (data as any)?.data ?? []
  const total: number = (data as any)?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT))

  const deleteMutation = useMutation({
    mutationFn: (id: string) => vendorsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
      setDeleteTarget(null)
    },
  })

  function openAdd() {
    setEditVendor(null)
    setFormOpen(true)
  }

  function openEdit(vendor: Vendor, e?: React.MouseEvent) {
    e?.stopPropagation()
    setEditVendor(vendor)
    setFormOpen(true)
  }

  function openDelete(vendor: Vendor, e: React.MouseEvent) {
    e.stopPropagation()
    setDeleteTarget(vendor)
  }

  function openDetail(vendor: Vendor) {
    setDetailId(vendor.id)
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              className="pl-8 h-8 text-sm"
              placeholder="Search vendors…"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <Select
            className="h-8 text-sm w-40"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value)
              setPage(1)
            }}
          >
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </div>
        <Button size="sm" onClick={openAdd}>
          <Plus className="mr-1.5 h-4 w-4" />
          Add Vendor
        </Button>
      </div>

      {/* Count */}
      {!isLoading && (
        <p className="text-xs text-muted-foreground">
          {total} vendor{total !== 1 ? 's' : ''}
          {debouncedSearch || category ? ' matching filters' : ''}
        </p>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="divide-y">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-3.5">
                  <Skeleton className="h-8 w-8 rounded-full shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-36" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-5 w-16 rounded-full" />
                  <Skeleton className="h-5 w-16 rounded-full" />
                </div>
              ))}
            </div>
          ) : !vendors.length ? (
            <div className="py-16 text-center">
              <Building2 className="mx-auto mb-3 h-10 w-10 text-muted-foreground/30" />
              <p className="font-medium text-sm">
                {debouncedSearch || category ? 'No vendors match' : 'No vendors yet'}
              </p>
              {!debouncedSearch && !category && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Click "Add Vendor" to get started.
                </p>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {vendors.map((v) => (
                <div
                  key={v.id}
                  className="flex items-center gap-4 px-5 py-3.5 cursor-pointer hover:bg-muted/40 transition-colors"
                  onClick={() => openDetail(v)}
                >
                  {/* Avatar */}
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                    <Building2 className="h-3.5 w-3.5 text-primary" />
                  </div>

                  {/* Name + bank */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm truncate">{v.name}</p>
                      {v.nickname && (
                        <span className="text-xs text-muted-foreground hidden sm:inline">
                          ({v.nickname})
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 font-mono">
                      {v.bankIfsc} · {v.bankAccountMasked}
                    </p>
                  </div>

                  {/* Bene badge */}
                  {v.beneId && (
                    <div title="ICICI Bene Registered">
                      <ShieldCheck className="h-4 w-4 text-emerald-500 shrink-0" />
                    </div>
                  )}

                  {/* Category */}
                  {categoryBadge(v.category)}

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      title="Edit"
                      onClick={(e) => openEdit(v, e)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      title="Delete"
                      onClick={(e) => openDelete(v, e)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <p className="text-muted-foreground text-xs">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-1">
            <Button
              size="icon"
              variant="outline"
              className="h-7 w-7"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              className="h-7 w-7"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Add / Edit Form */}
      <VendorForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['vendors'] })
        }}
        vendor={editVendor}
      />

      {/* Vendor Detail Panel */}
      <VendorDetail
        vendorId={detailId}
        open={!!detailId}
        onClose={() => setDetailId(null)}
        onEdit={(v) => {
          setDetailId(null)
          openEdit(v)
        }}
      />

      {/* Delete Confirm */}
      <DeleteDialog
        vendor={deleteTarget}
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        isPending={deleteMutation.isPending}
      />
    </div>
  )
}
